import React, { useState, useEffect, useCallback } from 'react';
import { 
  ISegmentComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import './df-form-segment.scss';

interface DfFormSegmentProps {
  id: string;
  properties: ISegmentComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormSegment: React.FC<DfFormSegmentProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  showCommentsInPreview = false
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  // Validate field
  const validateField = useCallback((value: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!value || value.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);


  // Handle segment button click
  const handleSegmentClick = useCallback((optionValue: string) => {
    setSelectedValue(optionValue);
    setIsTouched(true);
    
    if (onValueChange) {
      const validation = validateField(optionValue);
      const change: IFormControlChange = {
        id,
        value: optionValue,
        isValid: validation.isValid,
        errors: validation.errors
      } as IFormControlChange;
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (formValue !== selectedValue) {
      setSelectedValue(formValue);
    }
  }, [formValue, selectedValue]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get segment container class (matching Angular approach)
  const getSegmentContainerClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'grid-layout';
  };




  // Get button type for styling (pass/fail) - fallback for backward compatibility
  const getButtonType = (optionValue: string): 'pass' | 'fail' => {
    const value = optionValue?.toLowerCase();
    if (value === 'pass' || value === 'yes') {
      return 'pass';
    }
    if (value === 'fail' || value === 'no') {
      return 'fail';
    }
    return 'pass';
  };

  // Get button icon based on option - use custom icon if available, otherwise fallback
  const getButtonIcon = (option: any): string => {
    if (option?.icon !== undefined && option.icon !== '') {
      return option.icon;
    }
    // Fallback to old logic
    const buttonType = getButtonType(option?.value || option);
    return buttonType === 'pass' ? '✓' : '×';
  };

  // Get button styles based on option - use custom colors if available
  const getButtonStyles = (option: any, isSelected: boolean): React.CSSProperties => {
    const hasCustomColors = option?.color || option?.backgroundColor;
    
    if (hasCustomColors) {
      return {
        color: isSelected ? '#ffffff' : (option.color || '#00814b'),
        backgroundColor: isSelected ? (option.color || '#00814b') : (option.backgroundColor || '#34bd4914'),
        borderColor: option.color || '#00814b'
      };
    }
    
    // Fallback to old logic - no inline styles, use CSS classes
    return {};
  };

  // Get button class - use custom class if custom colors exist, otherwise use pass/fail
  const getButtonClass = (option: any): string => {
    const hasCustomColors = option?.color || option?.backgroundColor;
    
    if (hasCustomColors) {
      return 'segment-button-label segment-button-custom';
    }
    
    // Fallback to old logic
    const buttonType = getButtonType(option?.value || option);
    return `segment-button-label ${buttonType === 'pass' ? 'pass-button' : 'fail-button'}`;
  };

  const shouldDisable = disabled || readonly || properties?.validation?.readonly;
  return (
    <>
    <div className={`form-group segment-group ${getLabelAlignmentClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="form-segment-wrapper">
            <div className={`form-segment-container ${getSegmentContainerClass()}`}>
              {(properties.options || []).map((option, index) => {
                const isSelected = selectedValue === option.value;
                const buttonStyles = getButtonStyles(option, isSelected);
                const buttonClass = getButtonClass(option);
                const buttonIcon = getButtonIcon(option);
                
                return (
                  <div key={index} className={`form-segment-option ${isSelected ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      id={`${id}_option_${index}`}
                      name={id}
                      value={option.value}
                      checked={isSelected}
                      onChange={() => !shouldDisable && !option.disabled && handleSegmentClick(option.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      disabled={shouldDisable || option.disabled}
                      className="form-segment-radio"
                    />
                    <label
                      htmlFor={`${id}_option_${index}`}
                      className={buttonClass}
                      style={Object.keys(buttonStyles).length > 0 ? buttonStyles : undefined}
                    >
                      {buttonIcon && <span className="segment-icon">{buttonIcon}</span>}
                      <span className="segment-text">{option.value}</span>
                    </label>
                  </div>
                );
              })}
            </div>

          </div>

        </>
      ) : (
        <div className="form-segment-wrapper">
          <div className={`form-segment-container ${getSegmentContainerClass()}`}>
            {(properties.options || []).map((option, index) => {
              const isSelected = selectedValue === option.value;
              const buttonStyles = getButtonStyles(option, isSelected);
              const buttonClass = getButtonClass(option);
              const buttonIcon = getButtonIcon(option);
              
              return (
                <div key={index} className={`form-segment-option ${isSelected ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    id={`${id}_option_${index}`}
                    name={id}
                    value={option.value}
                    checked={isSelected}
                    onChange={() => !shouldDisable && !option.disabled && handleSegmentClick(option.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-segment-radio"
                  />
                  <label
                    htmlFor={`${id}_option_${index}`}
                    className={buttonClass}
                    style={Object.keys(buttonStyles).length > 0 ? buttonStyles : undefined}
                  >
                    {buttonIcon && <span className="segment-icon">{buttonIcon}</span>}
                    <span className="segment-text">{option.value}</span>
                  </label>
                </div>
              );
            })}
          </div>
          



        </div>
       )}

      
     </div>
     <DfFormErrorMsg
            validationErrors={validationErrors}
            fieldId={id}
            touchedFields={touchedFields}
            formSubmitted={formSubmitted}
            properties={properties}
            localValidation={validateField(selectedValue)}
            isTouched={isTouched}
            mode={mode}
          />
     </>
     
   );
};



export default DfFormSegment;
